import React, { useEffect, useState } from "react";
import { HiOutlineTicket } from "react-icons/hi";
import { Assort } from "../../../models/Assort";
import "./style.scss";
import "../../../../style/block.scss";
import "../../../../style/feature.scss";
import "../../../../style/hovers.scss";
import { BiInfoCircle } from "react-icons/bi";
import HtmlTooltip from "@mui/material/Tooltip";
import { TicketAccess } from "../../../models/TicketAccess";
import "../../../../style/buttons.scss";
import getId from "../../../../utils/getId";
import { BasketItem } from "../../../models/BasketItem";
import useDesktopSize from "../../../../utils/hooks/useDesktopSize";
import ModalComponent from "../../../../features/ModalComponent";
import { useTranslation } from "react-i18next";
import { QntStateItem } from "../../../models/QntStateItem";
import EAQuantitySelector from "../../shared/QuantitySelector/EAQuantitySelector";
import EAChip from "../../shared/Chip/EAChip";

interface IProps {
  ticketLimit?: number;
  item: Assort;
  exercise?: TicketAccess;
  preBasket: BasketItem[];
  addItemToBasket: (assort: Assort, exercise?: TicketAccess) => void;
  removeItemFromBasket: (assort: Assort, exercise?: TicketAccess) => void;
  setItemQuantity?: (assort: Assort, newQuantity: number, exercise?: TicketAccess) => void;
  qntState?: QntStateItem[];
  allowManualInput?: boolean;
  eventCapacityLimit?: number;
  assortSpecificLimit?: number | null; // limit specyficzny dla tego biletu (remainingQuantity z API)
} // optional for styling

const TicketLineSummary: React.FC<IProps> = ({
  item,
  exercise,
  preBasket,
  ticketLimit,
  addItemToBasket,
  removeItemFromBasket,
  setItemQuantity,
  qntState,
  allowManualInput = false,
  eventCapacityLimit,
  assortSpecificLimit,
}: IProps) => {
  const { t } = useTranslation();

  const getQnt = (value: Assort): number => {
    let result = 0;
    if (qntState && qntState.length > 0 && exercise) {
      const foundQnt = qntState.find(
        (item) => item.assortIds === getId(value) && item.exerciseIds === exercise.exerciseIds,
      );
      result = foundQnt ? foundQnt.count : 0;
    } else {
      const itemBasket = preBasket.find((item) => item.assortIds === getId(value));
      result = itemBasket ? itemBasket.qnt : 0;
    }

    return result;
  };

  useEffect(() => {
    if (item.maxSale === 0 || !item.maxSale) {
      setMaxQnt(99);
    } else {
      setMaxQnt(item.maxSale);
    }
    //eslint-disable-next-line
  }, []);
  const isDesktop = useDesktopSize();
  const [open, setOpen] = useState(false);
  const [maxQnt, setMaxQnt] = useState(0);
  const isDiscount = item.price !== item.listPrice;

  const ticketsInCurrentBasket = () => {
    if (qntState && qntState.length > 0 && exercise) {
      return qntState
        .filter((item) => item.exerciseIds === exercise.exerciseIds)
        .reduce((acc: number, item: QntStateItem) => acc + item.count, 0);
    } else {
      return preBasket.reduce((acc: number, item: BasketItem) => acc + item.qnt, 0);
    }
  };

  const handleQuantityChange = (newValue: number) => {
    if (!allowManualInput) return;

    if (setItemQuantity) {
      // Użyj bezpośredniego ustawienia ilości jeśli dostępne
      setItemQuantity(item, newValue, exercise);
    } else {
      // Fallback do starej logiki z pętlą (dla kompatybilności wstecznej)
      const currentQnt = getQnt(item);
      const diff = newValue - currentQnt;

      if (diff > 0) {
        for (let i = 0; i < diff; i++) {
          addItemToBasket(item, exercise);
        }
      } else if (diff < 0) {
        for (let i = 0; i < Math.abs(diff); i++) {
          removeItemFromBasket(item, exercise);
        }
      }
    }
  };

  const getEffectiveMax = () => {
    let effectiveMax = maxQnt;
    const currentQnt = getQnt(item);

    // 1) Limit specyficzny dla asortymentu (np. TourSale) – jeśli istnieje, zawsze zawęża max
    //    UWAGA: null = brak limitu, używamy maxQnt
    if (assortSpecificLimit !== undefined && assortSpecificLimit !== null) {
      effectiveMax = Math.min(effectiveMax, assortSpecificLimit);
    }

    // 2) ticketLimit (globalny limit na wydarzenie / komponent) – działa równolegle
    if (ticketLimit !== undefined) {
      const remainingTickets = ticketLimit - ticketsInCurrentBasket() + currentQnt;
      effectiveMax = Math.min(effectiveMax, remainingTickets);
    }

    // 3) eventCapacityLimit (globalna pojemność wydarzenia, np. capacity - reserved lub globalLimit)
    if (eventCapacityLimit !== undefined) {
      const remainingEventCapacity = eventCapacityLimit - ticketsInCurrentBasket() + currentQnt;
      effectiveMax = Math.min(effectiveMax, remainingEventCapacity);
    }

    return Math.max(0, effectiveMax); // Nie może być ujemne
  };

  const effectiveMax = getEffectiveMax();

  const itemName = item.displayWebName || item.name;

  return (
    <>
      <div
        className={
          getQnt(item) > 0
            ? "ticket-line-summary ticket-line-active ticket-line flex-between-center"
            : "ticket-line-summary ticket-line flex-between-center"
        }
      >
        <div className="ticket-line-left flex-start-center">
          <div className="ticket-line-name-container">
            <HiOutlineTicket className="ticket-line-icon" />
            <HtmlTooltip placement="top" arrow={true} title={itemName}>
              <b className="ticket-line-name">{itemName}</b>
            </HtmlTooltip>
          </div>

          <>
            {item.description.length > 2 && (
              <>
                {isDesktop ? (
                  <HtmlTooltip
                    placement="right"
                    arrow={true}
                    title={
                      <>
                        <article
                          className="tooltip-description"
                          dangerouslySetInnerHTML={{ __html: item.description }}
                        ></article>
                      </>
                    }
                  >
                    <b>
                      <BiInfoCircle className="tooltip-icon" />
                    </b>
                  </HtmlTooltip>
                ) : (
                  <b onClick={() => setOpen(!open)}>
                    <BiInfoCircle className="tooltip-icon" />
                  </b>
                )}
              </>
            )}
          </>
        </div>

        <div className="ticket-line-right flex-end-center">
          {effectiveMax === 0 && (
            <EAChip label={t("niedostepne")} variant="dark" className="ticket-line__unavailable-chip" />
          )}
          <div className="price-container">
            <b className={`price ${isDiscount ? "price-through" : ""}`}>
              {item.listPrice.toFixed(2)} {t("zl")}
            </b>
            {isDiscount && (
              <b className={`price ${isDiscount ? "price-through-color" : ""}`}>
                {item.price.toFixed(2)} {t("zl")}
              </b>
            )}
          </div>

          <div className="qnt flex-end-center">
            {/** W fallbacku (eventCapacityLimit) pokazujemy MAX z globalnej pojemności,
             * a w wariancie z limitami per asort (assortSpecificLimit) pokazujemy MAX z limitu.
             */}
            {/** Wyliczamy, czy pokazać informację MAX na podstawie dostępnych źródeł limitu */}
            {/*
              Uwaga: używamy zmiennych pomocniczych wewnątrz JSX, żeby zachować przejrzystość.
            */}
            <EAQuantitySelector
              value={getQnt(item)}
              onChange={handleQuantityChange}
              onIncrement={() => addItemToBasket(item, exercise)}
              onDecrement={() => removeItemFromBasket(item, exercise)}
              disabled={!allowManualInput}
              incrementDisabled={getQnt(item) >= effectiveMax}
              decrementDisabled={getQnt(item) <= 0}
              min={0}
              max={effectiveMax}
              showMaxInfo={
                (assortSpecificLimit !== undefined && assortSpecificLimit !== null && assortSpecificLimit > 0) ||
                (eventCapacityLimit !== undefined && eventCapacityLimit > 0 && eventCapacityLimit < maxQnt)
              }
              className="quantity-selector--end"
            />
          </div>
        </div>
      </div>

      {open && (
        <div className="schedule-modal-info">
          <ModalComponent
            footer={false}
            open={open}
            closeModal={() => setOpen(false)}
            title={item.name}
            onAccept={() => setOpen(false)}
            acceptText={t("przeczytalem")}
          >
            {item.description.length > 1 ? (
              <p className="news-modal-body" dangerouslySetInnerHTML={{ __html: item.description }}></p>
            ) : (
              <p className="news-modal-body">{t("brak_opisu")}</p>
            )}
          </ModalComponent>
        </div>
      )}
    </>
  );
};

export default TicketLineSummary;
